/**
 * LightSignIn — minimal email + name modal.
 *
 * Pops on first visit to any page that imports it. Captures the two
 * fields, derives a deterministic learner_id, syncs to backend, and
 * persists in localStorage so subsequent visits skip the modal.
 *
 * Designed to be friction-free: no password, no email verification,
 * no marketing consent checkbox. The privacy banner already covers
 * the analytics-consent piece; this is just attribution.
 */
import { useEffect, useState } from "react";
import { getIdentity, setIdentity } from "../lib/learnerIdentity";
import { verifyMasterCode } from "../lib/masterUnlock";

const ACCENT = "#6B8F71";
const ACCENT_OK = "#6B8F71";

export default function LightSignIn({ onSignedIn, forceOpen = false }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [learnerId, setLearnerId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [masterOpen, setMasterOpen] = useState(false);
  const [masterCode, setMasterCode] = useState("");
  const [masterErr, setMasterErr] = useState("");
  const [masterBusy, setMasterBusy] = useState(false);

  useEffect(() => {
    const id = getIdentity();
    if (forceOpen || !id) setOpen(true);
    if (id && onSignedIn) onSignedIn(id);
  }, [forceOpen, onSignedIn]);

  if (!open) return null;

  const valid = email.includes("@") && email.includes(".") && name.trim().length >= 2;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!valid || busy) return;
    setBusy(true); setErr("");
    try {
      const lid = learnerId.trim().toUpperCase();
      if (lid) {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mystery/learner-id/${encodeURIComponent(lid)}`);
        const d = await r.json();
        if (!r.ok) throw new Error("Learner ID not found — check it with your instructor, or leave it blank.");
        localStorage.setItem("mc_learner_id", d.learner_id);
        localStorage.setItem("mc_learner_name", d.name || d.learner_id);
      }
      const id = await setIdentity({ email, display_name: name });
      setOpen(false);
      if (onSignedIn) onSignedIn(id);
    } catch (ex) {
      setErr(String(ex.message || ex));
    } finally {
      setBusy(false);
    }
  };

  const handleMaster = async (e) => {
    e?.preventDefault();
    if (masterBusy || !masterCode) return;
    setMasterBusy(true); setMasterErr("");
    const res = await verifyMasterCode(masterCode);
    setMasterBusy(false);
    if (res.ok) {
      setOpen(false);
      try { window.dispatchEvent(new CustomEvent("maldek-identity-change", { detail: null })); } catch { /* noop */ }
      if (onSignedIn) onSignedIn(getIdentity());
    } else {
      setMasterErr("Code rejected.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: "rgba(2,6,18,0.75)", backdropFilter: "blur(8px)" }}
      data-testid="light-signin"
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-3xl border p-7"
        style={{
          background: `radial-gradient(circle at 0% 0%, ${ACCENT}22, transparent 55%), rgba(10,18,38,0.95)`,
          borderColor: `${ACCENT}44`,
          boxShadow: `0 20px 80px ${ACCENT}22`,
        }}
        data-testid="light-signin-form"
      >
        <p className="text-[10px] font-mono uppercase tracking-[0.28em] mb-3" style={{ color: ACCENT_OK }}>
          Welcome
        </p>
        <h2 className="font-heading text-3xl text-white font-bold tracking-tight mb-2">
          Quick sign-in.
        </h2>
        <p className="text-white/65 text-sm mb-6 leading-relaxed">
          Email and name — no password, no verification, no marketing. Add the
          learner ID your instructor gave you so your case work appears under
          your name.
        </p>

        <label className="block mb-3">
          <span className="text-[10px] font-mono uppercase tracking-widest text-white/65 mb-1 block">Email</span>
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@school.edu"
            data-testid="light-signin-email"
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/15 text-white text-sm focus:outline-none focus:border-cyan-400/60 transition-colors"
          />
        </label>

        <label className="block mb-3">
          <span className="text-[10px] font-mono uppercase tracking-widest text-white/65 mb-1 block">Your name</span>
          <input
            type="text"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Smith"
            data-testid="light-signin-name"
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/15 text-white text-sm focus:outline-none focus:border-cyan-400/60 transition-colors"
          />
        </label>

        <label className="block mb-5">
          <span className="text-[10px] font-mono uppercase tracking-widest text-white/65 mb-1 block">Learner ID · optional</span>
          <input
            type="text"
            value={learnerId}
            onChange={(e) => { setLearnerId(e.target.value.toUpperCase()); setErr(""); }}
            placeholder="e.g. AMA-4821 (from your instructor)"
            data-testid="light-signin-learner-id"
            className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/15 text-white text-sm focus:outline-none focus:border-cyan-400/60 transition-colors"
          />
        </label>

        {err && <p className="text-red-400 text-sm mb-3" data-testid="light-signin-error">{err}</p>}

        <button
          type="submit"
          disabled={!valid || busy}
          data-testid="light-signin-submit"
          className="w-full py-3 rounded-full text-sm font-mono uppercase tracking-widest font-bold disabled:opacity-40 transition-all"
          style={{
            background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT_OK})`,
            color: "#06121e",
            boxShadow: !valid ? "none" : `0 4px 20px ${ACCENT}55`,
          }}
        >
          {busy ? "Signing in…" : "Continue"}
        </button>

        <p className="text-[10px] text-white/40 mt-4 leading-relaxed">
          We use a SHA-256 hash of your email as your learner ID. You can
          clear your identity any time from the platform menu.
        </p>

        {/* Faculty / staff master-code override */}
        <div className="mt-5 pt-4 border-t border-white/10 text-center">
          {!masterOpen ? (
            <button
              type="button"
              onClick={() => setMasterOpen(true)}
              data-testid="light-signin-master-link"
              className="text-[11px] font-mono uppercase tracking-[0.22em] hover:opacity-80"
              style={{ color: "#7fb3dd" }}
            >
              Faculty / staff — I have a master code →
            </button>
          ) : (
            <div className="flex flex-col gap-2" data-testid="light-signin-master-form">
              <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/50">
                Master code · opens the learners & faculty view
              </p>
              <input
                type="password"
                value={masterCode}
                autoFocus
                inputMode="numeric"
                onChange={(e) => { setMasterCode(e.target.value); setMasterErr(""); }}
                placeholder="9-digit master code"
                data-testid="light-signin-master-input"
                className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/15 text-white text-base font-mono tracking-widest focus:outline-none focus:border-cyan-400/60 transition-colors text-center"
              />
              {masterErr && <p className="text-red-400 text-xs" data-testid="light-signin-master-error">{masterErr}</p>}
              <button
                type="button"
                onClick={handleMaster}
                disabled={masterBusy || !masterCode}
                data-testid="light-signin-master-submit"
                className="py-2.5 rounded-full text-xs font-mono uppercase tracking-widest font-bold disabled:opacity-40 transition-all"
                style={{ background: `linear-gradient(135deg, #6B8F71, ${ACCENT_OK})`, color: "#06121e" }}
              >
                {masterBusy ? "Verifying…" : "Unlock learners & faculty"}
              </button>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
